import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../api/apiFetch';
import { 
  X, RotateCw, CheckCircle, Plus, Edit2, Trash2, Save, ArrowRight 
} from 'lucide-react';
import { diffWords } from '../../utils/DiffUtils';
import './FactEvolutionModal.css';

interface HistoryLog {
  id: string;
  _id: string;
  mongo_id?: string;
  memory_id: string;
  old_memory: any;
  new_memory: any;
  event: string;
  created_at: string;
  actor_id: string;
  role?: string;
  session_id: string;
  is_deleted?: boolean;
}

interface FactGraph {
  nodes: any[];
  relationships: any[];
}

interface FactEvolutionModalProps {
  memoryId: string;
  onClose: () => void;
  onDataChanged?: () => void; // Notify parent to refresh if needed
}

const renderDiff = (oldVal: any, newVal: any, event: string) => {
  const oldText = !oldVal ? "" : (typeof oldVal === 'string' ? oldVal : JSON.stringify(oldVal));
  const newText = !newVal ? "" : (typeof newVal === 'string' ? newVal : JSON.stringify(newVal));
  const eventLower = event.toLowerCase();
  
  if (eventLower.includes('add') || eventLower.includes('extract')) {
    return <div className="diff-split-block added"><div className="diff-split-label">Extracted</div><div className="diff-split-content"><span className="diff-added-inline">{newText}</span></div></div>;
  }
  if (eventLower.includes('delete')) {
    return <div className="diff-split-block removed"><div className="diff-split-label">Deleted</div><div className="diff-split-content"><span className="diff-removed-inline">{oldText || newText}</span></div></div>;
  }
  
  if (eventLower.includes('update')) {
     const diff = diffWords(oldText, newText);
     return (
       <div className="trace-diff-split-container">
          <div className="diff-split-block removed">
             <div className="diff-split-label">Deleted</div>
             <div className="diff-split-content">
                {diff.map((part, i) => (
                  part.removed ? <span key={i} className="diff-removed-inline">{part.value}</span> : 
                  !part.added ? <span key={i} className="diff-context-dim">{part.value}</span> : null
                ))}
             </div>
          </div>
          <div className="diff-split-block added">
             <div className="diff-split-label">New State</div>
             <div className="diff-split-content">
                {diff.map((part, i) => (
                  part.added ? <span key={i} className="diff-added-inline">{part.value}</span> : 
                  !part.removed ? <span key={i}>{part.value}</span> : null
                ))}
             </div>
          </div>
       </div>
     );
  }

  return <span>{newText}</span>;
};

const FactEvolutionModal: React.FC<FactEvolutionModalProps> = ({ memoryId, onClose, onDataChanged }) => {
  const [evolution, setEvolution] = useState<HistoryLog[] | null>(null);
  const [graph, setGraph] = useState<FactGraph | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingText, setEditingText] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [evoResp, graphResp] = await Promise.all([
        apiFetch(`/api/v1/memory/facts/${memoryId}/evolution`),
        apiFetch(`/api/v1/memory/facts/${memoryId}/graph`)
      ]);

      if (evoResp.ok) {
        const json = await evoResp.json();
        setEvolution(Array.isArray(json) ? json : []);
      }
      if (graphResp.ok) {
        const json = await graphResp.json();
        setGraph(json);
      }
    } catch (err) {
      console.error('[FactEvolutionModal] Fetch failed:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (memoryId) fetchData();
  }, [memoryId]);

  const handleUpdate = async (newText: string) => {
    setIsSaving(true);
    try {
      const resp = await apiFetch(`/api/v1/memory/facts/${memoryId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_text: newText })
      });
      if (resp.ok) {
        setEditingText(null);
        await fetchData();
        if (onDataChanged) onDataChanged();
      } else {
        const err = await resp.json();
        alert(`Failed to update fact: ${err.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Update failed:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this fact?")) return;
    setIsDeleting(true);
    try {
      const resp = await apiFetch(`/api/v1/memory/facts/${memoryId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actor_id: "system", session_id: "manual_delete" })
      });
      if (resp.ok) {
        if (onDataChanged) onDataChanged();
        onClose();
      } else {
        const err = await resp.json();
        alert(`Failed to delete fact: ${err.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  return (
    <div className="evolution-modal-overlay" onClick={onClose}>
      <div className="evolution-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Fact Evolution: {memoryId}</h3>
          <button className="close-modal-btn" onClick={onClose} aria-label="Close Modal">
            <X size={20} />
          </button>
        </div>
        <div className="modal-body">
          {loading ? (
            <div className="modal-loading">
               <RotateCw size={24} className="animate-spin" />
               <span>Loading details...</span>
            </div>
          ) : (
            <div className="modal-scroll-area">
              {/* Graph Context Section */}
              <div className="graph-context-section">
                <h4 className="section-title">Graph Context</h4>
                {graph && graph.nodes && graph.nodes.length > 0 ? (
                  <>
                    <div className="graph-entities">
                      {graph.nodes.filter((n: any) => !n.labels.includes('Memory')).map((node: any, i: number) => (
                        <div key={i} className="entity-pill">
                          <span className="entity-label">{node.labels[0] || 'Entity'}</span>
                          <span className="entity-name-text">{node.name || node.text || node.id}</span>
                        </div>
                      ))}
                    </div>
                    {graph.relationships && graph.relationships.length > 0 ? (
                      <div className="graph-relationships">
                        {graph.relationships.map((rel: any, i: number) => {
                          const startNode = graph.nodes.find((n: any) => n.id === rel.start_node_id);
                          const endNode = graph.nodes.find((n: any) => n.id === rel.end_node_id);
                          return (
                            <div key={i} className="rel-item">
                              <span className="rel-node-name">{startNode?.name || startNode?.text || startNode?.id}</span>
                              <span className="rel-type">[{rel.type}]</span>
                              <span className="rel-node-name">{endNode?.name || endNode?.text || endNode?.id}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      graph.nodes.length > 1 && <div className="no-rels-message">No explicit relationships found.</div>
                    )}
                  </>
                ) : (
                  <div className="no-related-entities">No graph context found for this fact.</div>
                )}
              </div>

              {/* Evolution History Section */}
              <div className="evolution-section">
                <h4 className="section-title">Timeline Evolution</h4>
                {evolution && evolution.length > 0 ? (
                  <div className="evolution-list">
                    {evolution.map((step, idx) => {
                      const isLatest = idx === evolution.length - 1;
                      const isEditing = isLatest && editingText !== null;
                      const isDeleted = evolution.some(e => e.event === 'DELETE' || e.is_deleted);
                      
                      return (
                        <div key={step._id || idx} className="evolution-step">
                          <div className="step-marker">
                            <span className="step-marker-label">Step {idx + 1} {step.event}</span>
                            <div className={`step-icon-marker ${step.event.toLowerCase()}`}>
                              {step.event.toLowerCase().includes('add') ? <Plus size={10} strokeWidth={3} /> : 
                               step.event.toLowerCase().includes('update') ? <Edit2 size={10} strokeWidth={3} /> : 
                               step.event.toLowerCase().includes('delete') ? <X size={10} strokeWidth={3} /> : 
                               <div className="step-dot" />}
                            </div>
                            {idx < evolution.length - 1 && <div className="step-line" />}
                          </div>
                          <div className="step-content">
                            <div className="step-header">
                              <span className="step-date">{formatDate(step.created_at)}</span>
                              <span className="step-actor">by {step.actor_id}</span>
                              
                              {isLatest && !isEditing && !isDeleted && (
                                <div className="step-actions">
                                  <button className="icon-btn-edit" onClick={() => setEditingText(step.new_memory || step.old_memory || "")}>
                                    <Edit2 size={12} />
                                  </button>
                                  <button className="icon-btn-delete" onClick={handleDelete} disabled={isDeleting}>
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              )}
                            </div>
                            
                            <div className="evolution-history-content">
                              {isEditing ? (
                                <div className="inline-edit-area">
                                  <textarea 
                                    className="edit-textarea"
                                    value={editingText || ""}
                                    onChange={(e) => setEditingText(e.target.value)}
                                    autoFocus
                                  />
                                  <div className="edit-actions">
                                    <button className="save-btn" onClick={() => handleUpdate(editingText || "")} disabled={isSaving}>
                                      {isSaving && <RotateCw size={12} className="animate-spin" />} Save
                                    </button>
                                    <button className="cancel-btn" onClick={() => setEditingText(null)} disabled={isSaving}>
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                renderDiff(step.old_memory, step.new_memory, step.event)
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="no-evolution">No evolution history found.</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FactEvolutionModal;
